import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaGraduationCap, FaCalculator, FaBalanceScale, FaChartLine, FaLaptop, FaUsers } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const EtablissementScolaire = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaCalculator />,
            title: "Comptabilité, Finances et Fiscalité",
            description: "Gestion comptable, établissement des comptes annuels, gestion budgétaire et fiscale spécifiques aux établissements d'enseignement.",
            color: "#0d6efd"
        },
        {
            icon: <FaBalanceScale />,
            title: "Audit et Contrôle Interne",
            description: "Audit légal, contractuel et mise en place de procédures de contrôle interne adaptées à votre établissement d'enseignement.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Conseil et Accompagnement Stratégique",
            description: "Accompagnement à la création, développement et optimisation de votre établissement scolaire ou universitaire.",
            color: "#dc3545"
        },
        {
            icon: <FaGraduationCap />,
            title: "Formations",
            description: "Formations sur-mesure pour votre personnel administratif et vos équipes de direction en matière de comptabilité et gestion.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        comptabilite: [
            {
                title: "Tenue et révision comptable",
                description: "Gestion rigoureuse de votre comptabilité, en conformité avec les normes comptables et les instructions spécifiques."
            },
            {
                title: "Établissement des comptes annuels",
                description: "Bilan, compte de résultat, annexe, et production des documents financiers obligatoires."
            },
            {
                title: "Comptabilité analytique",
                description: "Mise en place d'outils de suivi précis de vos coûts par activité, par section, par projet, etc."
            },
            {
                title: "Gestion budgétaire",
                description: "Élaboration de budgets prévisionnels, suivi budgétaire rigoureux, analyse des écarts."
            },
            {
                title: "Gestion de la trésorerie",
                description: "Optimisation de vos flux de trésorerie, anticipation des besoins de financement."
            },
            {
                title: "Conseil fiscal",
                description: "Optimisation de votre situation fiscale (impôt sur les sociétés, taxe sur les salaires, etc.), gestion des exonérations spécifiques."
            },
            {
                title: "Déclarations fiscales et sociales",
                description: "Prise en charge de l'ensemble de vos déclarations."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail, accompagnement en cas de contrôle de la CNSS. Gestion des spécificités des conventions collectives de l'enseignement privé."
            },
            {
                title: "Suivi des subventions",
                description: "Aide à la constitution des dossiers, suivi des versements, justification des dépenses."
            }
        ],
        audit: [
            {
                title: "Audit légal (commissariat aux comptes)",
                description: "Certification de vos comptes annuels (si votre établissement est soumis à cette obligation)."
            },
            {
                title: "Audit contractuel",
                description: "Audit de vos procédures internes, de votre gestion financière, de vos projets spécifiques."
            },
            {
                title: "Mise en place et optimisation du contrôle interne",
                description: "Renforcement de la maîtrise de vos risques et de la sécurité de vos opérations."
            },
            {
                title: "Audit d'acquisition",
                description: "Si l'établissement envisage une opération de croissance externe."
            }
        ],
        conseil: [
            {
                title: "Accompagnement à la création et au développement",
                description: "Étude de faisabilité, business plan, choix de la structure juridique (association, société, fondation, etc.), recherche de financements."
            },
            {
                title: "Conseil en gestion financière",
                description: "Analyse de la rentabilité, optimisation des coûts, recherche de financements (emprunts, dons, mécénat, etc.)."
            },
            {
                title: "Conseil en gestion des ressources humaines",
                description: "Optimisation de la masse salariale, gestion des contrats, recrutement."
            },
            {
                title: "Accompagnement des projets immobiliers",
                description: "Analyse financière, recherche de financements, montage juridique."
            },
            {
                title: "Conseil en matière de tarification",
                description: "Définition de votre politique tarifaire, analyse de la structure de vos coûts."
            },
            {
                title: "Conseil en stratégie de développement",
                description: "Diversification des activités, développement de nouvelles offres de formation, internationalisation."
            },
            {
                title: "Accompagnement à la transformation digitale",
                description: "Mise en place d'outils numériques de gestion, de communication, et de pédagogie."
            },
            {
                title: "Conseil en matière de conformité réglementaire",
                description: "RGPD, accessibilité, sécurité..."
            }
        ],
        formation: [
            {
                title: "Formations sur-mesure",
                description: "Adaptées aux besoins de votre personnel administratif et de vos équipes de direction (comptabilité, finances, gestion budgétaire, réglementation, etc.)."
            },
            {
                title: "Formations inter- ou intra-établissements",
                description: "Pour vos collaborateurs."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">Expertise Comptable et Conseil pour les Établissements d'Enseignement</h1>
                            <p className="fs-5 mb-4">Optimisez Votre Gestion, Assurez Votre Conformité, Développez Vos Projets</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les enjeux spécifiques des établissements d'enseignement
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Les établissements scolaires et universitaires font face à des enjeux complexes : gestion rigoureuse des budgets,
                                respect des réglementations spécifiques, recherche de financements, développement de projets pédagogiques innovants...
                                CINEF-EC vous apporte une expertise dédiée pour vous accompagner dans ce contexte exigeant et vous permettre
                                de vous concentrer sur votre mission éducative.
                            </p>
                        </div>
                    </div>

                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Une expertise dédiée aux établissements d'enseignement</h3>
                            <p className="mb-4">
                                Notre cabinet dispose d'une expertise particulière dans l'accompagnement des établissements scolaires et universitaires.
                                Nous connaissons parfaitement les spécificités comptables, fiscales et juridiques liées à ce secteur.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous accompagner dans vos obligations légales et réglementaires
                                tout en vous aidant à développer votre établissement et à optimiser sa gestion.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaGraduationCap className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Écoles</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaUsers className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Universités</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaLaptop className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Centres de formation</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Comptabilité, Finances et Fiscalité</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.comptabilite.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`comptabilite-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Audit et Contrôle Interne</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.audit.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`audit-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Conseil et Accompagnement Stratégique</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.conseil.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`conseil-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Formations</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.formation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`formation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Vous souhaitez un accompagnement expert pour la gestion de votre établissement d'enseignement ?</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et personnalisée.
                                    Nous évaluerons ensemble vos besoins et vous proposerons des solutions adaptées à votre structure et à vos objectifs.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/education-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default EtablissementScolaire;